import { AlertTriangle, CheckCircle2, Info, TriangleAlert, X } from "lucide-react";
import { createContext, use, useCallback, useMemo, useState, type ReactNode } from "react";

import { playCue } from "@/lib/audio/tones";
import { cn } from "@/lib/cn";
import { describeError } from "@/lib/i18n/errors";
import { useT } from "@/lib/i18n";

type Tone = "info" | "success" | "warning" | "error";

type Toast = { id: number; tone: Tone; message: string };

type ToastApi = {
  show: (message: string, tone?: Tone) => void;

  fail: (error: unknown) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  error: AlertTriangle,
} as const;
const TONES: Record<Tone, string> = {
  info: "border-line bg-surface text-ink",
  success: "border-success/40 bg-success-soft text-ink",
  warning: "border-warning/50 bg-warning-soft text-ink",
  error: "border-danger/40 bg-danger-soft text-ink",
};

const LIFETIME_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const t = useT();

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (message: string, tone: Tone = "info") => {
      if (tone === "error") playCue("error");
      if (tone === "success") playCue("success");
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { id, tone, message }]);
      setTimeout(() => {
        dismiss(id);
      }, LIFETIME_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      fail: (error: unknown) => {
        show(describeError(error, t), "error");
      },
    }),
    [show, t],
  );

  return (
    <ToastContext value={api}>
      {children}
      {}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 top-4 z-[60] flex flex-col items-center gap-2 px-4"
      >
        {toasts.map((toast) => {
          const Icon = ICONS[toast.tone];
          return (
            <div
              className={cn(
                "pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-xl border px-4 py-3 shadow-overlay",
                TONES[toast.tone],
              )}
              key={toast.id}
              role={toast.tone === "error" || toast.tone === "warning" ? "alert" : "status"}
            >
              <Icon
                className={cn(
                  "mt-0.5 h-4 w-4 shrink-0",
                  toast.tone === "error"
                    ? "text-danger"
                    : toast.tone === "warning"
                      ? "text-warning"
                      : toast.tone === "success"
                        ? "text-success"
                        : "text-brand",
                )}
              />
              <p className="min-w-0 flex-1 text-sm">{toast.message}</p>
              <button
                aria-label={t("common.close")}
                className="-me-1 rounded-md p-1 text-ink-faint hover:text-ink"
                onClick={() => {
                  dismiss(toast.id);
                }}
                type="button"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext>
  );
}

export function useToast(): ToastApi {
  const api = use(ToastContext);
  if (api === null) throw new Error("useToast used outside ToastProvider");
  return api;
}
